import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import Seo from "@/components/Seo";
import Container from "@/components/marketing/Container";
import HeroBackground from "@/components/marketing/HeroBackground";
import { Calendar, ArrowRight, ChevronLeft, ChevronRight, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { slugify } from "@/lib/utils";
import Reveal from "@/components/marketing/Reveal";

const CATEGORY_LABELS = {
  fintech: "Fintech",
  crypto: "Crypto",
  tech: "Technology",
  real_estate: "Real Estate",
  luxury: "Luxury",
  general: "General",
};

const PER_PAGE = 9;

export default function Newsroom() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    base44.entities.Order
      .filter({ is_public: true }, "-actual_live_date", 100)
      .then((data) => {
        // Only show orders that have actually been published (exclude drafts, pending)
        const published = (data || []).filter(
          (o) => o.status && o.status !== "draft" && o.status !== "pending_payment" && o.status !== "submitted"
        );
        setOrders(published);
      })
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    return orders.filter((o) => o.news_category === filter);
  }, [orders, filter]);

  // Reset to page 1 when filter changes
  useEffect(() => { setPage(1); }, [filter]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="bg-canvas">
      <Seo
        title="Newsroom | Digital24"
        description="The Digital24 Newsroom — a live feed of press releases published and distributed through our platform across fintech, crypto, tech, real estate, luxury, and more."
      />

      {/* Hero */}
      <section className="relative bg-frame overflow-hidden">
        <HeroBackground />
        <Container className="relative pt-20 pb-16 lg:pt-28 lg:pb-20">
          <span className="inline-block font-mono text-xs uppercase tracking-widest px-2 py-1 border-[0.5px] border-[#3a3352] text-[#8a86a8] mb-6">
            NEWSROOM
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-[52px] font-bold text-white leading-[1.1] mb-6 max-w-3xl">
            The Digital24 Newsroom
          </h1>
          <p className="text-mist text-lg max-w-2xl mb-8 leading-[1.7]">
            A live reflection of every press release published through our platform — distributed across global media outlets, indexed in search, and fully attributed.
          </p>
          <div className="flex items-center gap-3 text-faded text-sm">
            <Newspaper className="w-4 h-4" />
            <span>{filtered.length} press release{filtered.length !== 1 ? "s" : ""} published</span>
          </div>
        </Container>
      </section>

      {/* Grid + Filters */}
      <section className="py-7 sm:py-9 lg:py-12">
        <Container>
          {/* Category Filter */}
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm transition-colors ${
                filter === "all" ? "bg-cobalt text-white" : "bg-white border border-border text-steel hover:text-bone"
              }`}
            >
              All
            </button>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm transition-colors ${
                  filter === key ? "bg-cobalt text-white" : "bg-white border border-border text-steel hover:text-bone"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-cobalt/20 border-t-cobalt rounded-full animate-spin" />
            </div>
          ) : paginated.length === 0 ? (
            <div className="text-center py-20 text-steel" aria-live="polite">
              <Newspaper className="w-12 h-12 mx-auto mb-4 text-steel/40" />
              <p className="font-heading text-xl text-bone mb-2">No press releases yet</p>
              <p className="text-sm">Check back soon — new releases are published regularly.</p>
            </div>
          ) : (
            <>
              <Reveal variant="card">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" aria-live="polite">
                {paginated.map((order) => {
                  const img = order.attachments?.[0]?.url || order.attachments?.[0];
                  const excerpt = (order.content || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 120);
                  return (
                    <Link
                      key={order.id}
                      to={`/newsroom/${order.id}-${slugify(order.title)}`}
                      className="group bg-white border border-border rounded-xl overflow-hidden hover:border-cobalt/30 hover:shadow-md transition-all flex flex-col"
                    >
                      {img && (
                        <div className="aspect-video overflow-hidden bg-slate">
                          <img
                            src={img}
                            alt={order.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-cobalt/10 text-cobalt">
                            {CATEGORY_LABELS[order.news_category] || "General"}
                          </span>
                          {order.actual_live_date && (
                            <span className="font-mono text-[10px] text-steel flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(order.actual_live_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading text-lg font-bold text-bone mb-2 group-hover:text-cobalt transition-colors line-clamp-2">
                          {order.title}
                        </h3>
                        {excerpt && (
                          <p className="text-sm text-steel leading-relaxed line-clamp-2 mb-4 flex-1">{excerpt}</p>
                        )}
                        <div className="flex items-center justify-between mt-auto pt-3 border-t border-border">
                          {order.client_brand_name ? (
                            <span className="text-xs font-medium text-bone truncate">{order.client_brand_name}</span>
                          ) : (
                            <span />
                          )}
                          <span className="inline-flex items-center gap-1 text-cobalt text-xs font-mono group-hover:translate-x-0.5 transition-transform">
                            Read <ArrowRight className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              </Reveal>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border bg-white text-steel hover:text-bone hover:border-cobalt/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`inline-flex items-center justify-center w-10 h-10 rounded-lg text-sm font-mono font-medium transition-colors ${
                        p === page
                          ? "bg-cobalt text-white"
                          : "bg-white border border-border text-steel hover:text-bone hover:border-cobalt/30"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border bg-white text-steel hover:text-bone hover:border-cobalt/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </Container>
      </section>
    </div>
  );
}